import bus from '@/EventBus'
import config from '@/config'
import OrderService from '@/services/OrderService'
import RouteService from '@/services/RouteService'
import LocationService from '@/services/LocationService'

// initial state
const state = {
	currentOrder: null,
	searchText: '',
	currentTab: null,
	viewMode: 'orders',
	filterCountry: null
}

// actions
const actions = {
	selectTab({ commit }, tabName) {
		commit('SET_CURRENT_TAB', tabName)
		bus.$emit('tab-change')
	},
	selectViewMode({ commit }, viewMode) {
		commit('SET_CURRENT_VIEW_MODE', viewMode)
		bus.$emit('change-view-mode')
	},
	selectFilterCountry({ commit }, filterCountry) {
		commit('SET_CURRENT_FILTER_COUNTRY', filterCountry)
		bus.$emit('change-filter-country')
	},
	search({ commit }, text) {
		commit('SET_SEARCH_TEXT', text)
		bus.$emit('search')
	},
	setInitialOrder({ commit }, user) {
		const order = {
			isInvoice: false,
			isApproved: false,
			isUrgent: false,
			status: 'pending',
			creator: user.id,
			broker: user.name,
			paymentVat: 'brutto 23%',
			currency: 'PLN',
			country: 'PL'
		}

		commit('SET_CURRENT_ORDER', order)
	},
	setCurrentOrder({ commit }, order) {
		if (order && !order.logs) {
			return OrderService.find(order.id).then(res => {
				commit('SET_CURRENT_ORDER', res.data['hydra:member'][0])
			})
		} else commit('SET_CURRENT_ORDER', order)
		commit('SET_CURRENT_ORDER', order)
	},
	updateOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	acceptOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'new')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	archiveOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'archived')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	unarchiveOrderToNew({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('REMOVE_ROUTE_FROM_ORDER')
		commit('SET_ORDER_STATUS', 'new')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	moveToTrash({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'trashed')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	moveToComplaint({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'complaint')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	unarchiveOrderToPending({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('REMOVE_ROUTE_FROM_ORDER')
		commit('SET_ORDER_STATUS', 'pending')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	createOrder({ commit, state }, { order }) {
		commit('SET_CURRENT_ORDER', order)
		return OrderService.create(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	addToExisitingRoute({ commit, state }, { order, route }) {
		commit('SET_CURRENT_ORDER', order)
		commit('ADD_ROUTE_TO_ORDER', route)
		commit('SET_ORDER_STATUS', route.status)
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	removeFromRoute({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('REMOVE_ROUTE_FROM_ORDER')
		commit('SET_ORDER_STATUS', 'new')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	addToNewRoute({ commit, state }, { order, routeName }) {
		commit('SET_CURRENT_ORDER', order)
		return RouteService.create({ name: routeName, status: 'planned' }).then(res => {
			commit('ADD_ROUTE_TO_ORDER', res.data)
			commit('SET_ORDER_STATUS', res.data.status)
			return OrderService.update(state.currentOrder).then(res => {
				commit('SET_CURRENT_ORDER', res.data)
				return state.currentOrder
			})
		})
	},
	deleteOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		return OrderService.destroy(state.currentOrder).then(() => {
			commit('SET_CURRENT_ORDER', null)
			return order
		})
	},
	findLocation() {
		return LocationService.find(state.currentOrder)
	},
	updateOrderLocation({ commit }, location) {
		commit('SET_ORDER_LOCATION', location)
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	clearLocation({ commit }) {
		commit('CLEAR_ORDER_LOCATION')
		return OrderService.update(state.currentOrder).then(res => {
			commit('SET_CURRENT_ORDER', res.data)
			return state.currentOrder
		})
	},
	sendEmail(_, data) {
		return OrderService.sendEmail(data)
	}
}

// mutations
const mutations = {
	SET_CURRENT_TAB(state, tabName) {
		state.currentTab = tabName
	},
	SET_CURRENT_VIEW_MODE(state, mode) {
		state.viewMode = mode
	},
	SET_CURRENT_FILTER_COUNTRY(state, filterCountry) {
		state.filterCountry = filterCountry
	},
	SET_SEARCH_TEXT(state, text) {
		state.searchText = text
	},
	SET_CURRENT_ORDER(state, order) {
		state.currentOrder = Object.assign({}, order) // remove reference
	},
	SET_NEW_ORDER_PARAMS(state, user) {
		state.currentOrder.isInvoice = false
		state.currentOrder.isApproved = false
		state.currentOrder.isUrgent = false
		state.currentOrder.status = 'pending'
		state.currentOrder.creator = user.id
		state.currentOrder.broker = user.name
		if (!config.company.isNettoBruttoEnabled) state.currentOrder.paymentVat = 'brutto'
		if (!config.company.isMultiCurrencyEnabled) state.currentOrder.currency = 'PLN'
	},
	ADD_ROUTE_TO_ORDER(state, route) {
		state.currentOrder.route = route
	},
	REMOVE_ROUTE_FROM_ORDER(state) {
		state.currentOrder.route = null
	},
	SET_ORDER_STATUS(state, status) {
		state.currentOrder.status = status
	},
	SET_ORDER_LOCATION(state, location) {
		state.currentOrder.lat = location.lat
		state.currentOrder.lon = location.lon
	},
	CLEAR_ORDER_LOCATION(state) {
		state.currentOrder.lat = null
		state.currentOrder.lon = null
	}
}

export default {
	namespaced: true,
	state,
	actions,
	mutations
}
